<template>
    <div class="question-review__container">
        <KeywordDefinition
            v-if="showKeywordDefinition && visibleKeywordDefinition && questionContainer && keywordDefinitionParams"
            :keywordDOMElement="keywordDefinitionParams.keywordDOMElement"
            :keywordClickLocation="keywordDefinitionParams.keywordClickLocation"
            :keyword="visibleKeywordDefinition.keyword"
            :definition="visibleKeywordDefinition.definition"
            :questionContainer="questionContainer"
            :serial="currentQuestion.serial"
            :keywordLocation="visibleKeywordDefinition.location"
            @close="showKeywordDefinition = false"
        />
        <QuizContainer
            ref="quizContainer"
            class="question-review"
            :show-side="showSideBar"
            :is-dark-mode="isDarkMode"
        >
            <template #header>
                <PocketButton
                    class="question-review__back-btn"
                    type="icon-yellow"
                    :is-dark-mode="isDarkMode"
                    @click="goBack"
                >
                    <Icon class="question-review__back-btn-icon" type="arrow" />
                    <template v-if="breakpoint !== 'black-bear'">
                        Back to
                    </template>{{ currentQuizId ? 'Results' : 'Review' }}
                </PocketButton>
                <div class="question-review__frame-context">
                    {{ frameContext }}
                </div>
                <div class="question-review__pagination">
                    <Icon
                        v-if="breakpoint !== 'black-bear'"
                        class="question-review__top-previous-arrow"
                        :class="{
                            'question-review__top-previous-arrow--disabled': isPreviousArrowDisabled,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <span class="question-review__page-nums">
                        {{ questionIndex + 1 }} / {{ listOfQuestions[filterType].length }}
                    </span>
                    <Icon
                        v-if="breakpoint !== 'black-bear'"
                        class="question-review__top-next-arrow"
                        :class="{
                            'question-review__top-next-arrow--disabled': isNextArrowDisabled,
                        }"
                        :tabindex="isNextArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
            </template>
            <template #side>
                <div v-if="showSideBar" class="question-review__side-bar">
                    <div class="question-review__side-bar-header">
                        Questions
                        <PocketButton
                            type="icon-yellow"
                            :is-dark-mode="isDarkMode"
                            :icon="(filterType !== 'all' || filterSubjects.length) ? 'filterActive' : 'filter'"
                            :aria-label="`${
                                (filterType !== 'all' || filterSubjects.length) ? 'Filters selected, ' : ''
                            }Show filters`"
                            @click="showFilterDropdown = !showFilterDropdown"
                            @keypress.enter="showFilterDropdown = !showFilterDropdown"
                            @mousedown.prevent
                        />
                        <FilterDropdown
                            v-if="showFilterDropdown"
                            class="question-review__filter-dropdown"
                            :default-question-type="filterType"
                            :default-subjects="filterSubjects"
                            aria-live="polite"
                            @filterChange="updateFilter"
                            @close="showFilterDropdown = false"
                        />
                    </div>
                    <div v-if="listOfQuestions[filterType].length" class="question-review__list">
                        <div
                            v-for="(q, index) in listOfQuestions[filterType]"
                            :key="q.serial"
                            tabindex="0"
                            class="question-review__list-prompt"
                            :class="{
                                'question-review__list-prompt--active': index === questionIndex,
                            }"
                            @click="listQuestionClick(q)"
                            @keydown.enter.stop.prevent="listQuestionClick(q)"
                            @mousedown.prevent
                            v-html="q.scenarioPartLabel ?
                                `${q.scenarioPartLabel}: ${q.prompt}` :
                                q.prompt"
                        />
                    </div>
                    <div v-else class="question-review__list-empty">
                        Looks like there aren’t any questions to match your filter criteria.
                    </div>
                </div>
            </template>
            <template #question>
                <Icon
                    v-if="isLoading"
                    v-dark
                    class="question-review__loading"
                    type="loading2"
                    :is-dark-mode="isDarkMode"
                />
                <Question
                    v-else-if="currentQuestion && currentExamMetadata"
                    :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}`"
                    class="question-review__question"
                    :ref="setQuestionContainerRef"
                    :container-el="quizContainerEl"
                    :question="currentQuestion"
                    :previous-choices="previousChoicesTyped"
                    :previous-matrix-choices="previousMatrixChoicesTyped"
                    :previous-build-list-choices="previousBuildListChoicesTyped"
                    :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
                    :is-dark-mode="isDarkMode"
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    :image-url-prefix="`${s3Url || ''}/${currentQuestion.compositeKey.toUpperCase()}/`"
                    :review-mode="true"
                    :default-show-explanation="
                        (breakpoint === 'black-bear' || breakpoint === 'brown-bear') ? false : true
                    "
                    :global-metrics="showGlobalMetrics ? currentGlobalMetric : null"
                    :auto-focus-prompt="true"
                    :show-paywall="showPaywall"
                    :hide-references="currentExamMetadata.hideReferences"
                    :keyword-definitions="keywordDefinitions"
                    @upgrade="openUpgradeModal"
                    @keywordClick="keywordClicked"
                    @close="goBack"
                    @previousQuestion="clickPrevious"
                    @nextQuestion="clickNext"
                    @update:showExplanation="explanationViewToggled"
                >
                    <template #explanationTopExperiment>
                        <KeywordDefinitionBanner
                            :isDarkMode="isDarkMode"
                            v-if="[0, 4, 7].includes(seenReviewWithKeywordsCount)
                                && !hasClickedKeyword
                                && !hasSeenHighlightedKeyword
                                && keywordDefinitions.length"
                        />
                    </template>
                    <template #context>
                        <div v-dark class="question-review__subject">
                            {{ currentQuestionSubject }}
                        </div>
                    </template>
                </Question>
                <div v-else class="question-review__no-question">
                    <div class="question-review__no-question-title">
                        Nothing to show here.
                    </div>
                    <div class="question-review__no-question-subtext">
                        Select a question and let’s get back to studying.
                    </div>
                </div>
            </template>
            <template #footer>
                <div v-if="breakpoint !== 'black-bear'" class="question-review__view-toggles">
                    <div
                        class="question-review__side-toggle"
                    >
                        <div
                            v-dark
                            class="question-review__view-side"
                            :class="{
                                'question-review__view-side--active': showSideBar,
                                'question-review__view-side--hover': listViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show List View"
                            :aria-checked="showSideBar"
                            @mouseenter="listViewMouseEnter"
                            @mouseleave="listViewMouseLeave"
                            @mousedown.prevent
                            @focus="showListViewTooltip = true"
                            @blur="showListViewTooltip = false"
                            @click="showSideBar = true"
                            @keydown.enter.stop.prevent="showSideBar = true"
                        >
                            <Tooltip
                                v-if="showListViewTooltip"
                                :is-dark-mode="isDarkMode"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider,
                                    'color': brandColors.brandBlack,
                                }"
                            >
                                List View
                            </Tooltip>
                            <Icon
                                v-if="showSideBar || listViewHover"
                                class="question-review__view-side-active-icon"
                                type="sideBarActive"
                            />
                            <Icon
                                v-else
                                class="question-review__view-side-icon"
                                type="sideBar"
                            />
                        </div>
                        <div
                            v-dark
                            class="question-review__view-full"
                            :class="{
                                'question-review__view-full--active': !showSideBar,
                                'question-review__view-full--hover': fullViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show Full View"
                            :aria-checked="!showSideBar"
                            @mouseenter="fullViewMouseEnter"
                            @mouseleave="fullViewMouseLeave"
                            @mousedown.prevent
                            @focus="showFullViewTooltip = true"
                            @blur="showFullViewTooltip = false"
                            @click="showSideBar = false"
                            @keydown.enter.stop.prevent="showSideBar = false"
                        >
                            <Tooltip
                                v-if="showFullViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider,
                                    'color': brandColors.brandBlack,
                                }"
                            >
                                Full View
                            </Tooltip>
                            <Icon
                                v-if="!showSideBar || fullViewHover"
                                class="question-review__view-full-active-icon"
                                type="fullViewActive"
                            />
                            <Icon
                                v-else
                                class="question-review__view-full-icon"
                                type="fullView"
                            />
                        </div>
                    </div>
                    <div>
                        <GlobalMetricsToggle
                            :disabled="showPaywall"
                            class="question-review__global-metrics-toggle-left"
                            :show-global-metrics="showGlobalMetrics"
                            :is-dark-mode="isDarkMode"
                            @toggleGlobalMetrics="toggleGlobalMetrics"
                        />
                    </div>
                </div>
                <div class="question-review__controls">
                    <Icon
                        class="question-review__previous-arrow"
                        :class="{
                            'question-review__previous-arrow--disabled': isPreviousArrowDisabled,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <GlobalMetricsToggle
                        v-if="breakpoint === 'black-bear'"
                        :disabled="showPaywall"
                        class="question-review__global-metrics-toggle-center"
                        :show-global-metrics="showGlobalMetrics"
                        :is-dark-mode="isDarkMode"
                        @toggleGlobalMetrics="toggleGlobalMetrics"
                    />
                    <FlagToggle
                        :key="`flagToggle_${allowKeyboardShortcuts}`"
                        class="question-review__flag-toggle"
                        :is-flagged="isFlagged"
                        :enable-flag-tooltip="breakpoint !== 'brown-bear' && breakpoint !== 'black-bear'"
                        :enable-flag-keyboard-shortcut="allowKeyboardShortcuts"
                        @toggleFlag="toggleFlag"
                    />
                    <Icon
                        tabindex="0"
                        class="question-review__next-arrow"
                        :class="{
                            'question-review__next-arrow--disabled': isNextArrowDisabled,
                        }"
                        type="paginationArrow"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton
                    class="question-review__keyboard-shortcuts-btn"
                    :is-dark-mode="isDarkMode"
                    :tooltip-theme="
                        (breakpoint === 'polar-bear' || breakpoint === 'grizzly-bear' || breakpoint === 'brown-bear')
                            ? 'leftalign'
                            : undefined
                    "
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="questionReviewKeyboardShortcutsModal"
                                keyboard-mode="review"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                :is-dark-mode="isDarkMode"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </template>
        </QuizContainer>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { globalQuestionMetricModule } from '@/store/globalQuestionMetric/module'
import { questionModule } from '@/store/question/module'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { screenModule } from '@/store/screen/module'
import FilterDropdown from '@/components/QuestionReview/FilterDropdown.vue'
import { qotdModule } from '@/store/qotd/module'
import type { Study } from '@pocketprep/types'
import type { ClientBundle } from '@/store/bundle/state'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { keywordsModule } from '@/store/keywords/module'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import KeywordDefinition from '@/components/KeywordDefinitions/KeywordDefinition.vue'
import KeywordDefinitionBanner from '@/components/KeywordDefinitions/KeywordDefinitionBanner.vue'
import { stripHtmlTags } from '@/utils'
import { upgradeModule } from '@/store/upgrade/module'

type TQuestionRow = {
    serial: string
    prompt: string
    isFlagged: boolean
    isCorrect: boolean
    scenarioPartLabel: string
}

type TQuestionsLib = {
    [key: string]: TQuestionRow[]
}

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const {
    QuizContainer,
    Button: PocketButton,
    Question,
    FlagToggle,
    KeyboardShortcutsButton,
    Icon,
    GlobalMetricsToggle,
    Tooltip,
    KeyboardShortcutsModal,
} = UIKit

const route = useRoute()
const router = useRouter()

const quizContainer = ref<InstanceType<typeof UIKit.QuizContainer> | null>(null)
const questionContainerRef = ref<InstanceType<typeof UIKit.Question> | null>(null)

const setQuestionContainerRef = (el: unknown) => {
    questionContainerRef.value = el as InstanceType<typeof UIKit.Question> | null
}

const isLoading = ref(true)
const showSideBar = ref(true)
const showGlobalMetrics = ref(false)
const listViewHover = ref(false)
const fullViewHover = ref(false)
const showListViewTooltip = ref(false)
const showFullViewTooltip = ref(false)
const filterSubjects = ref<string[]>([])
const filterType = ref('all')
const showFilterDropdown = ref(false)
const brandColors = BrandColors
const isFlagged = ref(false)
const isTogglingFlag = ref(false)
const keywordDOMElement = ref<HTMLElement | null>(null)
const keywordClickLocation = ref<{ x: number; y: number } | null>(null)
const showKeywordDefinition = ref(false)
const visibleKeywordDefinition = ref<{
    keyword: string
    definition: string
    location: string
} | null>(null)
const hasSeenHighlightedKeyword = ref(false)
const currentBundle = ref<null | Study.Class.BundleJSON | ClientBundle>(null)

const keywordDefinitionParams = computed(() => {
    const el = keywordDOMElement.value
    const loc = keywordClickLocation.value

    if (!el || !loc) return null
    return {
        keywordDOMElement: el,
        keywordClickLocation: loc,
    }
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const currentQuizId = computed(() => {
    return route.query.quizId as string
})

const currentSerial = computed(() => {
    return route.query.questionSerial as string
})

const currentQuestion = computed(() => {
    return questions.value[currentSerial.value]
})

const isTodaysQotD = computed(() => {
    return qotdModule.getters.getQotDQuestion()?.serial === currentQuestion.value?.serial
})

const showPaywall = computed(() => {
    return !hasActiveSubscription.value && !currentQuestion.value?.isFree && !isTodaysQotD.value
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const questionContainer = computed(() => {
    const refVal = questionContainerRef.value
    return refVal && '$el' in refVal ? refVal.$el as HTMLElement : null
})

const quizContainerEl = computed(() => {
    const refVal = quizContainer.value
    return (refVal && '$el' in refVal) ? refVal.$el : null
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const frameContext = computed(() => {
    return filterType.value === 'all'
        ? 'All Answered'
        : filterType.value
})

const currentQuiz = computed(() => {
    if (currentQuizId.value) {
        return quizModule.getters.getQuizById(currentQuizId.value) || null
    } else {
        return null
    }
})

const isMockExam = computed(() => {
    return currentQuiz.value?.mode === 5
})

const answers = computed(() => {
    if (currentQuizId.value && isMockExam.value) {
        return quizModule.getters.getLatestAnswers({ quizId: currentQuizId.value, questionFilter: 'mock' })
    } else if (currentQuizId.value) {
        return quizModule.getters.getLatestAnswers({ quizId: currentQuizId.value, questionFilter: 'all' })
    } else {
        return quizModule.getters.getLatestAnswers({ questionFilter: 'all' })
    }
})

const currentAnswer = computed(() => {
    return currentQuestion.value && answers.value[currentQuestion.value.serial]
})

const previousChoices = computed(() => {
    return currentAnswer.value?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const questions = computed(() => {
    return quizModule.getters.getAnsweredQuestions().reduce<{ [serial: string]: Study.Class.QuestionJSON }>(
        (acc, q) => {
            acc[q.serial] = q
            return acc
        }, {})
})

const seenReviewWithKeywordsCount = computed(() => {
    return userModule.state.user?.webConfig?.seenReviewWithKeywordsCount || 0
})

const hasClickedKeyword = computed(() => {
    return userModule.state.user?.webConfig?.hasClickedKeyword
})

const currentQuizQuestionSerials = computed(() => {
    return quizModule.getters.getQuizById(currentQuizId.value)?.answers.map(a => a.questionSerial) || []
})

const answeredQuestionsSerialLib = computed(() => {
    const lib = quizModule.getters.getAnsweredQuestions().reduce((acc, q) => {
        if (!acc[q.serial]) {
            acc[q.serial] = q
        }
        return acc
    }, {} as { [serial: string]: Study.Class.QuestionJSON })

    return lib
})

const flaggedQuestionSerials = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()?.flaggedQuestions || []
})

const quizOrReviewQuestions = computed(() => {
    if (currentQuizId.value) {
        return quizModule.getters.getQuizById(currentQuizId.value)?.answers.map(a => a) || []
    }

    return Object.values(quizModule.getters.getLatestAnswers({ questionFilter: 'all' }))
        .sort((a, b) => b.answerDate - a.answerDate)
})

const listOfQuestions = computed(() => {
    return quizOrReviewQuestions.value.reduce<TQuestionsLib>((acc, answer) => {
        const serial = answer.questionSerial
        const question = answeredQuestionsSerialLib.value[serial]
        if (!question) {
            return acc
        }

        const questionIsFlagged = !!flaggedQuestionSerials.value.includes(serial)
        const isCorrect = answer.isCorrect
        const subject = question.subject as Study.Class.SubjectJSON

        const prompt = stripHtmlTags(question.prompt)
        const scenarioPartLabelVal = scenarioPartLabel(question)

        if (filterSubjects.value.length && !filterSubjects.value.includes(subject.name)) {
            return acc
        }

        const questionObject = {
            serial,
            prompt,
            isFlagged: questionIsFlagged,
            isCorrect,
            scenarioPartLabel: scenarioPartLabelVal,
        }

        if (questionIsFlagged) {
            acc.flagged?.push(questionObject)
        }
        if (isCorrect) {
            acc.correct?.push(questionObject)
        }
        if (!isCorrect) {
            acc.incorrect?.push(questionObject)
        }

        acc.all?.push(questionObject)
        return acc
    }, { all: [], flagged: [], correct: [], incorrect: [] })
})

const previousQuestion = computed(() => {
    return listOfQuestions.value[filterType.value][questionIndex.value - 1]
})

const currentQuestionSubject = computed(() => {
    const subject = currentQuestion.value.subject as Study.Class.SubjectJSON
    return subject.name
})

const nextQuestion = computed(() => {
    return listOfQuestions.value[filterType.value][questionIndex.value + 1]
})

const questionIndex = computed(() => {
    return listOfQuestions.value[filterType.value].findIndex(q => q.serial === currentSerial.value)
})

const isPreviousArrowDisabled = computed(() => {
    return questionIndex.value <= 0
})

const isNextArrowDisabled = computed(() => {
    return questionIndex.value >= (listOfQuestions.value[filterType.value].length - 1)
})

const globalMetricsBySerial = computed(() => {
    return globalQuestionMetricModule.getters.getGlobalQuestionMetricsBySerial()
})

const currentGlobalMetric = computed(() => {
    if (!globalMetricsBySerial.value) {
        return null
    } else {
        return globalMetricsBySerial.value[currentSerial.value]
    }
})

const flaggedQuestions = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const keywordDefinitions = computed(() => {
    const questionSerial = currentQuestion.value?.serial
    const questionKeywords = keywordsModule.getters.getKeywordDefinitions().get(questionSerial)

    if (
        !currentAnswer.value
        || !questionKeywords
        || !currentAnswer.value
    ) {
        return []
    }

    return Array.from(questionKeywords, ([ keyword, definition ]) => ({
        keyword,
        definition,
    }))
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
        quizModule.actions.fetchAnsweredQuestions(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        globalQuestionMetricModule.actions.fetchGlobalQuestionMetrics(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
        bundleModule.actions.fetchBundles(),
    ])

    if (route.query.subject) {
        if (typeof route.query.subject === 'string') {
            filterSubjects.value = [ route.query.subject ]
        } else {
            filterSubjects.value = route.query.subject as string[]
        }
    }

    if (typeof route.query.type === 'string') {
        filterType.value = route.query.type.toLowerCase()
    }

    await keywordsModule.actions.fetchKeywordDefinitions([
        ...currentQuizQuestionSerials.value,
        ...Object.keys(answeredQuestionsSerialLib.value),
    ])

    const questionSerial = currentQuestion.value?.serial
    isFlagged.value = !!(questionSerial && flaggedQuestions.value.includes(questionSerial))
    currentBundle.value = bundleModule.getters.getCurrentBundle() || null
    // close side panel on `esc` press
    window.addEventListener('keydown', keydownListener, true)

    isLoading.value = false
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keydownListener, true)

    // if user was exposed to a highlighted keyword, update webConfig item
    if (hasSeenHighlightedKeyword.value || keywordDefinitions.value.length) {
        userModule.actions.updateWebConfig({
            seenReviewWithKeywordsCount: seenReviewWithKeywordsCount.value + 1,
        })
    }
})

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !showFilterDropdown.value) {
        e.stopPropagation()
        e.preventDefault()
        goBack()
    }
}

const goBack = () => {
    router.push(
        currentQuizId.value
            ? {
                name: 'quiz-result',
                params: {
                    quizId: currentQuizId.value,
                },
                query: route.query,
            }
            : { name: 'review', query: route.query }
    )
}

const toggleGlobalMetrics = (newVal: boolean) => {
    showGlobalMetrics.value = newVal
}

const clickPrevious = () => {
    if (previousQuestion.value) {
        router.replace({
            name: 'question-review',
            query: {
                ...route.query,
                questionSerial: previousQuestion.value.serial,
            },
        })
    }

    if (keywordDefinitions.value.length) {
        hasSeenHighlightedKeyword.value = true
    }
}

const clickNext = () => {
    if (nextQuestion.value) {
        router.replace({
            name: 'question-review',
            query: {
                ...route.query,
                questionSerial: nextQuestion.value.serial,
            },
        })
    }

    if (keywordDefinitions.value.length) {
        hasSeenHighlightedKeyword.value = true
    }
}

const keywordClicked = (params?: {
    keyword: string
    location: string
    clickLocation: { x: number; y: number }
    target: HTMLElement
}) => {
    showKeywordDefinition.value = false
    if (!params?.keyword) {
        return
    }

    const questionKeywords = keywordsModule.getters.getKeywordDefinitions().get(currentQuestion.value.serial)
    const definition = questionKeywords?.get(params.keyword.toLowerCase())

    if (!definition) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Keyword Definition Viewed', {
        examGuid: currentExamMetadata.value?.examGuid,
        definitionKeyword: params.keyword,
        serial: currentQuestion.value.serial,
        location: params.location,
        quizMode: currentQuiz.value?.mode ?? 10000,
        studyMode: 'Review',
    })

    visibleKeywordDefinition.value = {
        keyword: params.keyword,
        definition,
        location: params.location,
    }
    keywordDOMElement.value = params.target
    keywordClickLocation.value = params.clickLocation

    userModule.actions.updateWebConfig({ hasClickedKeyword: true })

    nextTick(() => {
        showKeywordDefinition.value = true
    })
}

const toggleFlag = async () => {
    // Prevent if already processing toggleQuestionFlag call
    if (isTogglingFlag.value) {
        return
    }

    isTogglingFlag.value = true
    isFlagged.value = !isFlagged.value
    const serial = currentQuestion.value?.serial

    try {
        await userExamMetadataModule.actions.toggleQuestionFlag(serial)
    } catch (e) {
        // noop
    } finally {
        isFlagged.value = !!(serial && flaggedQuestions.value.includes(serial))
        isTogglingFlag.value = false
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

/* istanbul ignore next */
const listViewMouseEnter = () => {
    listViewHover.value = true
    showListViewTooltip.value = true
}

/* istanbul ignore next */
const listViewMouseLeave = () => {
    listViewHover.value = false
    showListViewTooltip.value = false
}

/* istanbul ignore next */
const fullViewMouseEnter = () => {
    fullViewHover.value = true
    showFullViewTooltip.value = true
}

/* istanbul ignore next */
const fullViewMouseLeave = () => {
    fullViewHover.value = false
    showFullViewTooltip.value = false
}

const listQuestionClick = (q: TQuestionRow) => {
    if (q.serial !== currentSerial.value) {
        router.replace({
            name: 'question-review',
            query: {
                ...route.query,
                questionSerial: q.serial,
            },
        })
    }
    if (keywordDefinitions.value.length) {
        hasSeenHighlightedKeyword.value = true
    }
}

const updateFilter = ({ questionType, subjects }: { questionType?: string; subjects: string[] }) => {
    filterType.value = questionType || 'all'
    filterSubjects.value = subjects
    router.replace({
        name: 'question-review',
        query: {
            ...route.query,
            subject: filterSubjects.value,
            type: filterType.value.charAt(0).toUpperCase() + filterType.value.slice(1),
            questionSerial: questionIndex.value === -1
                ? listOfQuestions.value[filterType.value].length
                    ? listOfQuestions.value[filterType.value][0].serial
                    : undefined
                : route.query.questionSerial,
        },
    })
}

const scenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    return questionModule.getters.getQuestionScenarioPartLabel(question)
}

const explanationViewToggled = (showExplanation: boolean) => {
    const rightSideExplanationSection = document.getElementsByClassName('uikit-question-explanation')[0]
    // Do not log that the explanation was opened if the explanation is mounted "opened" on the right side
    if (showExplanation && !rightSideExplanationSection) {
        analyticsModule.actions.amplitudeTrack(
            'Explanation Opened',
            {
                examGuid: currentExamMetadata.value?.examGuid,
                serial: currentQuestion.value.serial,
                quizMode: typeof currentQuiz.value?.mode === 'number' ? currentQuiz.value?.mode : 10000,
                quizPlatform: 'Web',
                isSharedQotd: false,
                isReviewMode: true,
            }
        )
    }
}

watch(currentQuestion, () => {
    isFlagged.value = !!currentQuestion.value && flaggedQuestions.value.includes(currentQuestion.value.serial)
})
</script>

<style lang="scss" scoped>
.question-review {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    &__back-btn-icon {
        width: 14px;
        height: 12px;
        transform: rotate(180deg);
        margin-right: 8px;
    }

    &__frame-context {
        position: absolute;
        text-transform: capitalize;
        left: 50%;
        transform: translateX(-50%);
        color: $white;
        font-size: 15px;
        letter-spacing: -0.09px;
        line-height: 18px;
    }

    &__subject {
        width: 100%;
        max-width: 492px;
        color: $slate-01;
        margin-top: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        box-sizing: border-box;
        font-size: 14px;

        &--dark {
            color: $pewter;
        }

        @include breakpoint(black-bear) {
            padding-left: 15px;
            box-sizing: border-box;
        }
    }

    &__pagination {
        display: flex;
        align-items: center;
    }

    &__page-nums {
        margin: 0 7px;
    }

    &__side-bar {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__filter-dropdown {
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 2;
    }

    &__active-indicator {
        position: absolute;
        right: -10px;
        transform: translateY(-50%);
        width: 10px;
        height: 18px;
        background-color: $ash;
        border: 5px solid white;
    }

    &__side-bar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
        padding: 0 6px 0 12px;
        font-weight: 600;
        font-size: 16px;
        line-height: 19px;
    }

    &__list {
        position: relative;
        overflow-y: auto;
        flex: 1;
    }

    &__list-empty {
        color: $white;
        font-size: 13px;
        max-width: 160px;
        margin: 58px auto 0;
        line-height: 18px;
        opacity: 0.82;
        text-align: center;
    }

    &__list-prompt {
        position: relative;
        box-sizing: border-box;
        color: rgba($white, 0.75);
        padding: 14px 11px 14px 12px;
        font-size: 14px;
        letter-spacing: -0.09px;
        line-height: 17px;
        cursor: pointer;
        border-top: 2px solid $jet;
        outline: none;

        &:last-child {
            border-bottom: 2px solid $mariner;
        }

        &--active {
            color: $white;
            background-color: $ash;
            border-radius: 6px;
            border-color: $charcoal;

            &:last-child {
                border-bottom: 0;
            }
        }

        &--active + & {
            border-top-color: $charcoal;
        }

        &:hover {
            color: $white;
        }

        &:focus {
            color: $white;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border-radius: 6px;
                border: 1px solid $butterscotch;
            }
        }
    }

    &__loading {
        position: absolute;
        left: 50%;
        top: 25%;
        transform: translateX(-50%) scale(1.5);
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__no-question {
        max-width: 242px;
        margin: 158px auto 0;
        text-align: center;
    }

    &__no-question-title {
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 9px;
        font-weight: 600;
    }

    &__no-question-subtext {
        font-size: 14px;
        line-height: 17px;
        max-width: 242px;
        color: $ash;
    }

    &__view-toggles {
        display: flex;
        align-items: center;
    }

    &__side-toggle {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 80px;
        height: 37px;
        margin-right: 25px;
        border-radius: 6px;

        &::after {
            content: '';
            position: absolute;
            height: 100%;
            right: -13px;
            border-right: 1px solid $slate-03;
        }

        @include breakpoint(black-bear) {
            position: static;
        }
    }

    &__view-side,
    &__view-full {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 37px;
        border-radius: 6px;
        outline: none;

        &--hover {
            background-color: rgba($white, 0.1);
        }

        &--active {
            background-color: $charcoal;
            z-index: 2;

            &--dark {
                background-color: transparent;
            }
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 40px;
            height: 36px;
            top: 0;
            left: 0;
            border-radius: 4px;
            box-sizing: border-box;
        }

        &:not(&--active) {
            cursor: pointer;
        }

        .uikit-tooltip {
            position: absolute;
            left: calc(50%);
            top: calc(-100%);
        }
    }

    &__view-side {
        left: 0;

        &--hover {
            &:not(&--active) {
                padding-right: 6px;
                border-radius: 6px 0 0 6px;
            }

            &--dark {
                &:not(&--active) {
                    padding-right: 0;
                    border-radius: 6px;
                }
            }
        }
    }

    &__view-full {
        right: 0;

        &--hover {
            &:not(&--active) {
                padding-left: 6px;
                border-radius: 0 6px 6px 0;

                &:focus::before {
                    left: 6px;
                }
            }

            &--dark {
                &:not(&--active) {
                    padding-left: 0;
                    border-radius: 6px;
                }
            }
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        @include breakpoint(black-bear) {
            justify-content: space-between;
            width: 304px;
        }
    }

    &__previous-arrow,
    &__next-arrow,
    &__top-previous-arrow,
    &__top-next-arrow {
        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__top-previous-arrow,
    &__top-next-arrow {
        width: 24px;
        height: 24px;
    }

    &__previous-arrow,
    &__top-previous-arrow {
        transform: rotate(180deg);
    }

    &__global-metrics-toggle-center {
        margin-left: 62px;

        @include breakpoint(black-bear) {
            margin: 0;
        }
    }

    &__flag-toggle {
        margin: 0 80px;

        @include breakpoint(black-bear) {
            margin: 0;
        }
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    :deep(.uikit-quiz-container__main) {
        @include breakpoint(black-bear) {
            height: calc(100% - 99px);
        }
    }

    :deep(.uikit-question__close-icon) {
        display: none;
    }
}
</style>
